// MIT License - Copyright (c) 2026 Doug McNall
import { useState } from 'react';
import './AdminLogin.css';

const apiUrl = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL;

/**
 * Admin self-service password reset request.
 *
 * Posts to /auth/forgot-password and shows a generic success message
 * regardless of whether the email exists, mirroring the gateway's
 * email-enumeration defense.
 */
const ForgotPassword = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      const r = await fetch(`${apiUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setError(data.detail || 'Failed to send reset email.');
        return;
      }
      setSubmitted(true);
    } catch {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="admin-login">
        <div className="admin-login-container">
          <div className="admin-login-header">
            <h1>D&D Hot Sauce</h1>
            <h2>Check Your Email</h2>
          </div>
          <p style={{ color: 'var(--admin-text-secondary)', lineHeight: 1.6 }}>
            If an admin account exists for <strong>{email}</strong>, we've sent
            password reset instructions.
          </p>
          <p style={{ color: 'var(--admin-text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>
            The link expires in 1 hour. Check your spam folder if you don't see it.
          </p>
          <button
            className="login-button"
            style={{ marginTop: '1.5rem' }}
            onClick={onBack}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login">
      <div className="admin-login-container">
        <div className="admin-login-header">
          <h1>D&D Hot Sauce</h1>
          <h2>Forgot Password?</h2>
          <p style={{ color: 'var(--admin-text-secondary)', fontSize: '0.95rem' }}>
            Enter your admin email and we'll send a reset link.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="admin@ddhotsauce.com"
              disabled={submitting}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="login-button" disabled={submitting}>
            {submitting ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <div className="admin-login-footer">
          <button
            type="button"
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--admin-interactive-primary)',
              cursor: 'pointer',
              fontSize: '0.9rem',
              textDecoration: 'underline',
              padding: 0,
            }}
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
